/**
 * useModelCarousel — Multi-Model Carousel Hook
 *
 * Loads the ordered list of model summaries for the StartScreen carousel.
 *
 * Data flow:
 *   1. Read URL params: ?brand=, ?market=, ?models=, ?model=
 *   2. Fetch market.json → availableModels (source of truth)
 *   3. Apply optional ?models= URL override (subset/reorder)
 *   4. Determine initialIndex from ?model= param
 *   5. Fetch all ModelSummary data in parallel:
 *        - /content/models/{brand}/{id}/model.json   → displayName, thumbnail
 *        - /content/intersections/{brand}/{id}/{market}.json → consumptionDisclaimer
 *   6. Expose next/prev/goTo navigation with infinity loop
 *   7. Sync ?model= URL param on navigation via history.replaceState
 *
 * @see docs/ACTIVE_TASK.md — carousel architecture decisions
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ModelSummary, CarouselDirection } from '../types/carousel';

// ---------------------------------------------------------------------------
// URL param helpers
// ---------------------------------------------------------------------------

function getCarouselParams() {
  const p = new URLSearchParams(window.location.search);
  return {
    brand: p.get('brand') ?? 'audi',
    market: p.get('market') ?? 'de',
    /** ?model= sets the initial model id */
    initialModel: p.get('model') ?? null,
    /**
     * ?models= comma-separated override list, e.g. "rs5limo,q3sportback".
     * When absent, falls back to market.availableModels.
     */
    modelsOverride: p.get('models')?.split(',').filter(Boolean) ?? null,
    /** ?preset= activates a preset that may restrict availableModels. */
    preset: p.get('preset') ?? null,
    /** ?lang= override for UI strings (e.g. "en" on a German market). */
    lang: p.get('lang') ?? null,
  };
}

function syncModelParam(modelId: string) {
  const url = new URL(window.location.href);
  url.searchParams.set('model', modelId);
  history.replaceState(null, '', url.toString());
}

// ---------------------------------------------------------------------------
// Minimal JSON fetch helpers
// ---------------------------------------------------------------------------

/** Load JSON from /content/*. Vite dev middleware serves from monorepo root. */
async function fetchJson<T>(path: string): Promise<T> {
  const res = await fetch(path);
  if (!res.ok) throw new Error(`Failed to load ${path}: ${res.status}`);
  return res.json() as Promise<T>;
}

interface MarketJson {
  availableModels: string[];
  steeringWheel?: 'lhd' | 'rhd';
  logoLinkUrl?: string;
  ui?: Record<string, string>;
  uiByLang?: Record<string, Record<string, string>>;
  [key: string]: unknown;
}
interface PresetJson {
  availableModels?: string[];
  [key: string]: unknown;
}
type ThumbnailField = string | { lhd: string; rhd?: string } | undefined;
interface ModelJson {
  displayName: string;
  displayNameByLang?: Record<string, string>;
  assets?: { thumbnail?: ThumbnailField };
  [key: string]: unknown;
}
interface IntersectionJson {
  status?: 'live' | 'draft';
  consumptionDisclaimer?: string | false;
  consumptionDisclaimerByLang?: Record<string, string | false>;
  [key: string]: unknown;
}

/** Resolve a thumbnail field (string or lhd/rhd object) to a single URL. */
function resolveThumbnail(raw: ThumbnailField, side: 'lhd' | 'rhd'): string | undefined {
  if (!raw) return undefined;
  if (typeof raw === 'string') return raw;
  return side === 'rhd' && raw.rhd ? raw.rhd : raw.lhd;
}

/** Load the ModelSummary for one model id in parallel. */
async function loadModelSummary(
  brand: string,
  market: string,
  id: string,
  side: 'lhd' | 'rhd',
  lang: string | null,
): Promise<ModelSummary> {
  const [model, intersection] = await Promise.all([
    fetchJson<ModelJson>(`/content/models/${brand}/${id}/model.json`),
    fetchJson<IntersectionJson>(`/content/intersections/${brand}/${id}/${market}.json`).catch(
      () => ({}) as IntersectionJson,
    ),
  ]);

  const displayName = (lang ? model.displayNameByLang?.[lang] : undefined) ?? model.displayName;
  const consumptionDisclaimer =
    (lang ? intersection.consumptionDisclaimerByLang?.[lang] : undefined) ??
    intersection.consumptionDisclaimer;

  return {
    id,
    displayName,
    thumbnail: resolveThumbnail(model.assets?.thumbnail, side),
    consumptionDisclaimer,
    status: intersection.status,
  };
}

// ---------------------------------------------------------------------------
// Hook state
// ---------------------------------------------------------------------------

export interface UseModelCarouselResult {
  /** Ordered list of model summaries. Empty while loading. */
  summaries: ModelSummary[];
  loading: boolean;
  error: string | null;

  /** Zero-based index of the currently displayed model. */
  currentIndex: number;
  /** Active navigation direction — null when idle. */
  direction: CarouselDirection | null;
  /** True while the CSS transition is still running. Nav is blocked. */
  isTransitioning: boolean;

  /** Go to the next model (infinity loop). No-op while transitioning. */
  next: () => void;
  /** Go to the previous model (infinity loop). No-op while transitioning. */
  prev: () => void;
  /** Jump to a specific index by index. No-op while transitioning or same index. */
  goTo: (index: number) => void;

  /** Convenience: currently displayed model summary (or undefined while loading). */
  current: ModelSummary | undefined;
  /** URL to open when the brand logo is tapped — from market.logoLinkUrl. */
  logoLinkUrl: string | undefined;
  /** Display name of the brand, e.g. "Audi" — from brand.json. */
  brandName: string;
  /** UI translation strings from market.ui — with safe fallbacks. */
  marketUi: { appTitle: string; appSubtitle: string; startButton: string };
}

/** Duration of the CSS transition in ms — must match ModelCard.tsx. */
const TRANSITION_DURATION_MS = 380;

export function useModelCarousel(): UseModelCarouselResult {
  const [summaries, setSummaries] = useState<ModelSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [logoLinkUrl, setLogoLinkUrl] = useState<string | undefined>(undefined);
  const [brandName, setBrandName] = useState<string>('Audi');
  const [marketUi, setMarketUi] = useState({
    appTitle: 'Immersive Studio',
    appSubtitle: 'Experience the model interactively',
    startButton: 'Start',
  });

  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState<CarouselDirection | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  /** Ref so navigate callbacks don't go stale. */
  const summariesRef = useRef(summaries);
  const isTransRef = useRef(isTransitioning);
  summariesRef.current = summaries;
  isTransRef.current = isTransitioning;

  // -------------------------------------------------------------------------
  // Data loading
  // -------------------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const { brand, market, initialModel, modelsOverride, preset, lang } = getCarouselParams();

        // 1. Always load market.json — needed for steeringWheel and availableModels.
        //    Optionally load preset JSON (silent fallback on failure).
        //    Also load brand.json for the brand display name.
        const [mkt, brandData, presetData] = await Promise.all([
          fetchJson<MarketJson>(`/content/markets/${brand}/${market}.json`),
          fetchJson<{ displayName?: string }>(`/content/brands/${brand}/brand.json`).catch(() => ({
            displayName: undefined,
          })),
          preset
            ? fetchJson<PresetJson>(`/content/brands/${brand}/presets/${preset}.json`).catch(
                () => null,
              )
            : Promise.resolve(null),
        ]);
        const side: 'lhd' | 'rhd' = mkt.steeringWheel ?? 'lhd';
        // When no ?lang= param is set, use the market's base language (from locale, e.g. "fr-LU" → "fr")
        // so that displayNameByLang.fr is resolved for French markets without an explicit lang param.
        const baseLang = (mkt as { locale?: string }).locale?.split('-')[0]?.toLowerCase() ?? null;
        const effectiveLang = lang ?? baseLang;

        if (brandData.displayName) setBrandName(brandData.displayName);
        if (mkt.logoLinkUrl) setLogoLinkUrl(mkt.logoLinkUrl);
        if (mkt.ui) {
          const baseUi = mkt.ui;
          const langOverride =
            effectiveLang && mkt.uiByLang?.[effectiveLang] ? mkt.uiByLang[effectiveLang] : {};
          const merged = { ...baseUi, ...langOverride };
          setMarketUi({
            appTitle: merged['appTitle'] ?? 'Immersive Studio',
            appSubtitle: merged['appSubtitle'] ?? 'Experience the model interactively',
            startButton: merged['startButton'] ?? 'Start',
          });
        }

        // 2. Resolve ordered model id list.
        //    Priority: ?models= URL override > preset.availableModels > market.availableModels
        let modelIds: string[];
        if (modelsOverride && modelsOverride.length > 0) {
          modelIds = modelsOverride;
        } else if (presetData?.availableModels && presetData.availableModels.length > 0) {
          modelIds = presetData.availableModels;
        } else {
          modelIds = mkt.availableModels;
        }

        // 3. Load all summaries in parallel
        const loaded = await Promise.all(
          modelIds.map((id) => loadModelSummary(brand, market, id, side, effectiveLang)),
        );

        if (cancelled) return;

        // 3b. Two independent gates decide whether a model is offered:
        //     (a) editorial release state — `draft` is deliberately withheld, not a defect
        //     (b) legal completeness — a live model must carry its consumption disclaimer
        //     `consumptionDisclaimer === false` means "no disclaimer legally required here"
        //     and passes (b); an empty string does NOT — see content health check.
        const complete = loaded.filter((s) => {
          if (s.status === 'draft') return false;
          if (s.consumptionDisclaimer === false) return true;
          if (!s.consumptionDisclaimer) {
            console.warn(
              `[IMS] Model "${s.id}" hidden in market "${market}": status is "live" but ` +
                `consumptionDisclaimer is missing. Set it (or \`false\` if no disclaimer is ` +
                `legally required) in content/intersections/${brand}/${s.id}/${market}.json`,
            );
            return false;
          }
          return true;
        });

        // 4. Determine initial index
        let initialIndex = 0;
        if (initialModel) {
          const idx = complete.findIndex((s) => s.id === initialModel);
          if (idx >= 0) initialIndex = idx;
        }

        setSummaries(complete);
        setCurrentIndex(initialIndex);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load models');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []); // params are fixed for session lifetime

  // -------------------------------------------------------------------------
  // Navigation
  // -------------------------------------------------------------------------

  const navigate = useCallback((newIndex: number, dir: CarouselDirection) => {
    if (isTransRef.current) return;
    if (summariesRef.current.length === 0) return;

    setDirection(dir);
    setIsTransitioning(true);
    setCurrentIndex(newIndex);

    syncModelParam(summariesRef.current[newIndex].id);

    // Clear transitioning flag after CSS transition completes
    setTimeout(() => {
      setDirection(null);
      setIsTransitioning(false);
    }, TRANSITION_DURATION_MS);
  }, []);

  const next = useCallback(() => {
    const len = summariesRef.current.length;
    if (len === 0) return;
    const newIndex = (currentIndex + 1) % len;
    navigate(newIndex, 'right');
  }, [currentIndex, navigate]);

  const prev = useCallback(() => {
    const len = summariesRef.current.length;
    if (len === 0) return;
    const newIndex = (currentIndex - 1 + len) % len;
    navigate(newIndex, 'left');
  }, [currentIndex, navigate]);

  const goTo = useCallback(
    (index: number) => {
      if (index === currentIndex) return;
      const dir: CarouselDirection = index > currentIndex ? 'right' : 'left';
      navigate(index, dir);
    },
    [currentIndex, navigate],
  );

  return {
    summaries,
    loading,
    error,
    currentIndex,
    direction,
    isTransitioning,
    next,
    prev,
    goTo,
    current: summaries[currentIndex],
    logoLinkUrl,
    brandName,
    marketUi,
  };
}
